import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { TestMark } from './TestMark';
import './company-picker.css';

/** A company as the picker lists it: its slug, its name, and whether it is one of Ademero's test companies. */
export interface PickerCompany {
  id: string;
  name: string;
  testCompany: boolean;
}

export interface CompanyPickerProps {
  /** The company chosen, or null for all of them. */
  value: { id: string; name: string } | null;
  onChange: (company: PickerCompany | null) => void;
  /** Asks the server (`GET /v1/admin/companies?q=`): at most twenty answers, the newest when `q` is empty. */
  search: (q: string) => Promise<PickerCompany[]>;
  /** What "no company" is called here. */
  allLabel?: string;
  /** How long typing rests before the server is asked. */
  debounceMs?: number;
}

interface Answer {
  /** What was typed when this was asked. */
  query: string;
  rows: PickerCompany[];
}

/** The answer for what is typed, asked once typing rests; a slower earlier answer never replaces a later one. */
function useAnswers(
  search: CompanyPickerProps['search'],
  query: string,
  open: boolean,
  debounceMs: number,
): { rows: PickerCompany[]; busy: boolean } {
  const [answer, setAnswer] = useState<Answer | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    let current = true;
    const settle = (rows: PickerCompany[]): void => {
      if (current) setAnswer({ query, rows });
    };
    const timer = setTimeout(
      () => void search(query.trim()).then(settle, () => settle([])),
      query ? debounceMs : 0,
    );
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [search, query, open, debounceMs]);
  return { rows: answer?.rows ?? [], busy: answer?.query !== query };
}

/** What the open list says under its rows. */
function noteOf(busy: boolean, found: number, query: string): string {
  if (busy) return 'Looking';
  if (found === 0) return 'No company by that name, address or id.';
  return query
    ? 'Type more to narrow: twenty at most are listed.'
    : 'The newest companies. Type a name, a web address, an email domain or an id.';
}

/** The picker's state: whether it is open, what is typed, which row the keyboard is on. */
function usePicker(props: CompanyPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const { rows, busy } = useAnswers(props.search, query, open, props.debounceMs ?? 200);
  // Row 0 clears; the companies follow.
  const count = rows.length + 1;
  const close = (): void => {
    setOpen(false);
    setQuery('');
    setActive(0);
  };
  const pick = (index: number): void => {
    props.onChange(index === 0 ? null : (rows[index - 1] ?? null));
    close();
  };
  const type = (text: string): void => {
    setOpen(true);
    setActive(text ? 1 : 0);
    setQuery(text);
  };
  const move = (by: number): void =>
    open ? setActive((a) => (a + by + count) % count) : setOpen(true);
  const onKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      move(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      pick(active);
    }
  };
  return { open, setOpen, query, active, rows, busy, close, pick, type, onKey };
}

/**
 * The admin area's one company picker (Dustin, 2026-10-07: "imagine we have a thousand customers; a dropdown
 * to scroll through with no filter, no search"). A box to type in: the server answers by name, slug, the start
 * of an id or a member's email domain, twenty at most; opened empty it lists the newest companies. Arrow keys
 * move, Enter picks, Escape closes, and the first row clears back to all companies. Test companies carry
 * their mark. Use it wherever an admin screen is narrowed to one company; never a `select` of companies.
 */
export function CompanyPicker(props: CompanyPickerProps): ReactNode {
  const { value, allLabel = 'All companies' } = props;
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  const p = usePicker(props);
  return (
    <div
      className="ad-picker"
      ref={box}
      onBlur={(e) => {
        if (!box.current?.contains(e.relatedTarget)) p.close();
      }}
    >
      <input
        className="ad-picker__input"
        role="combobox"
        aria-label="Company"
        aria-expanded={p.open}
        aria-controls={`${id}-list`}
        aria-activedescendant={p.open ? `${id}-${p.active}` : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder={value ? value.name : allLabel}
        value={p.open ? p.query : (value?.name ?? '')}
        onFocus={() => p.setOpen(true)}
        onChange={(e) => p.type(e.currentTarget.value)}
        onKeyDown={p.onKey}
      />
      {p.open ? <Answers id={id} allLabel={allLabel} p={p} /> : null}
    </div>
  );
}

/** The open list: the row that clears, the companies the server answered, and a line saying what is listed. */
function Answers({
  id,
  allLabel,
  p,
}: {
  id: string;
  allLabel: string;
  p: ReturnType<typeof usePicker>;
}): ReactNode {
  return (
    <ul className="ad-picker__list" id={`${id}-list`} role="listbox" aria-label="Companies">
      <Option id={`${id}-0`} active={p.active === 0} onPick={() => p.pick(0)}>
        {allLabel}
      </Option>
      {p.rows.map((c, i) => (
        <Option
          key={c.id}
          id={`${id}-${i + 1}`}
          active={p.active === i + 1}
          onPick={() => p.pick(i + 1)}
        >
          <span>{c.name}</span>
          {c.testCompany ? <TestMark /> : null}
        </Option>
      ))}
      <li className="ad-picker__note" role="presentation">
        {noteOf(p.busy, p.rows.length, p.query)}
      </li>
    </ul>
  );
}

function Option({
  id,
  active,
  onPick,
  children,
}: {
  id: string;
  active: boolean;
  onPick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <li
      id={id}
      role="option"
      aria-selected={active}
      className={active ? 'ad-picker__option ad-picker__option--active' : 'ad-picker__option'}
      // The press, not the click: the input's blur would close the list before a click landed.
      onMouseDown={(e) => {
        e.preventDefault();
        onPick();
      }}
    >
      {children}
    </li>
  );
}
